import { useState, type FormEvent } from "react";
import { serializingManga, completedManga } from "@/mocks/manga";

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const [activeTab, setActiveTab] = useState<"连载中" | "完结" | "全部">("连载中");
  const [showSearch, setShowSearch] = useState(false);

  const totalSerializing = serializingManga.length;
  const totalCompleted = completedManga.length;

  const filterBy = (list: typeof serializingManga) => {
    const base = activeTab === "全部" ? list : list.filter((m) => m.status === activeTab);
    const kw = keyword.trim();
    if (!kw) return base;
    return base.filter(
      (m) => m.title.includes(kw) || m.category.includes(kw) || m.tags.some((t) => t.includes(kw)),
    );
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    setShowSearch(true);
  };

  const results = filterBy([...serializingManga, ...completedManga]);

  return (
    <section id="top" className="relative overflow-hidden">
      {/* background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20warm%20illustration%20of%20a%20giant%20snow-covered%20mountain%20behind%20a%20pile%20of%20colorful%20manga%20comic%20books%2C%20floating%20cherry%20blossom%20petals%20and%20snow%20flakes%20in%20the%20air%2C%20vermilion%20coral%20red%20and%20amber%20gold%20color%20palette%2C%20soft%20flat%20vector%20illustration%20style%2C%20clean%20composition&width=1600&height=900&seq=hero-chuixue&orientation=landscape"
          alt="吹雪漫画网首屏背景：雪山下堆满漫画书的温暖插画"
          title="吹雪漫画网 - 连载中完结漫画进度追更"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/40 to-background-950"></div>
      </div>

      <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 pb-16 pt-32 text-center md:px-6 md:pb-24 md:pt-44">
        <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs text-background-100 ring-1 ring-background-100/30 backdrop-blur-sm">
          <i className="ri-snowy-line"></i>
          吹雪漫画网 · 连载中 / 完结漫画进度一览
        </span>

        <h1 className="max-w-3xl font-heading text-3xl leading-snug text-background-50 md:text-5xl md:leading-snug">
          追更怕断？吹雪帮你盯紧每一话
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
          连载中、完结漫画进度一目了然，最新话更新提醒，点开即知、收藏更方便。无论你在追哪部热血连载，还是翻找心仪的完结作品，吹雪都让进度清清楚楚、追更不迷路。
        </p>

        <div className="mt-8 flex w-full max-w-lg items-center gap-3 rounded-lg bg-background-50 p-1.5 ring-1 ring-background-100/60">
          <span className="pl-2 text-foreground-400">
            <i className="ri-search-line text-lg"></i>
          </span>
          <form onSubmit={handleSubmit} className="flex flex-1 items-center" role="search">
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索漫画名、分类或标签…"
              aria-label="搜索漫画"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-8 md:gap-14">
          <div>
            <p className="font-heading text-3xl text-accent-500 md:text-4xl">{totalSerializing}</p>
            <p className="mt-1 text-xs text-background-200">部连载跟进中</p>
          </div>
          <div className="h-10 w-px bg-background-100/30"></div>
          <div>
            <p className="font-heading text-3xl text-accent-500 md:text-4xl">{totalCompleted}</p>
            <p className="mt-1 text-xs text-background-200">部完结作品</p>
          </div>
          <div className="h-10 w-px bg-background-100/30"></div>
          <div>
            <p className="font-heading text-3xl text-accent-500 md:text-4xl">24h</p>
            <p className="mt-1 text-xs text-background-200">最新话更新提醒</p>
          </div>
        </div>

        {/* quick search results */}
        {showSearch && (
          <div className="mt-8 w-full max-w-3xl rounded-lg bg-background-50 p-4 text-left shadow-none ring-1 ring-background-200">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-medium text-foreground-950">
                关于“{keyword}”的搜索结果（{results.length}）
              </p>
              <div className="flex gap-1 rounded-md bg-background-100 p-1">
                {(["全部", "连载中", "完结"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setActiveTab(t)}
                    className={`rounded px-2.5 py-1 text-xs transition ${
                      activeTab === t
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {results.length === 0 ? (
              <p className="py-6 text-center text-sm text-foreground-500">
                没有找到相关作品，试试其他关键词吧。
              </p>
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2">
                {results.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center gap-3 rounded-md bg-background-100/60 p-2.5"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-background-50 ${
                        m.status === "连载中" ? "bg-primary-500" : "bg-foreground-700"
                      }`}
                    >
                      <i className="ri-book-open-line"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-950">{m.title}</p>
                      <p className="truncate text-xs text-foreground-500">
                        {m.category} · {m.latestChapter}
                      </p>
                    </div>
                    <span className="ml-auto whitespace-nowrap text-xs text-foreground-400">
                      {m.progress}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <a
          href="#serializing"
          className="mt-10 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition hover:bg-accent-600"
        >
          浏览热门连载
          <i className="ri-arrow-down-line"></i>
        </a>
      </div>
    </section>
  );
}